import { defineComponent as _defineComponent } from 'vue'
import { isTouchScreen } from '@@/bits/browser'
import { dir } from '@@/bits/current_dir'
import { tailwindFlattenedColors } from '@@/bits/tailwind'
import { getTailwindColorClass } from '@@/bits/whiteboard'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import type { WhiteboardSecondaryMenu } from '@@/types/whiteboard'
import WhiteboardSvgIcon, { SVG_ICONS } from '@@/vuecomponents/WhiteboardSvgIcon.vue'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { offset } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import { computed, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'WhiteboardToolbarIcon',
  props: {
    colorScheme: { default: 'dark' },
    colorStyle: { default: 'frosted' },
    iconName: { default: 'cursor' },
    iconColor: { default: null },
    currentToolColor: { default: null },
    isSelected: { type: Boolean, default: false },
    label: { default: '' },
    shortcut: { default: '' },
    xTooltip: { type: Boolean, default: false },
    secondaryMenu: { default: undefined },
    secondarySelectionId: { default: undefined },
    disabled: { type: Boolean, default: false }
  },
  emits: ['hoveron', 'hoveroff', 'click'],
  setup(__props: any, { emit }) {

const props = __props as { colorScheme: 'dark' | 'light', colorStyle: 'frosted' | 'opaque', iconName: string, iconColor: string | null, currentToolColor: string | null, isSelected: boolean, label: string, shortcut: string, xTooltip: boolean, secondaryMenu: WhiteboardSecondaryMenu, secondarySelectionId: string, disabled: boolean };

const whiteboardToolbarStore = useWhiteboardToolbarStore()
const whiteboardStore = useWhiteboardStore()



const isHovered = ref(false)
const isActive = ref(false)
function onPointerEnter(): void {
  isHovered.value = true
  emit('hoveron')
}
function onPointerLeave(): void {
  isHovered.value = false
  emit('hoveroff')
}

function onPointerDown(event: PointerEvent | MouseEvent): void {
  isActive.value = true
  emit('click', event)
  // To prevent the button from staying focused after the click
  // So that when user use a keyboard shortcut to trigger another button, this button will become :focus-visible
  unfocusButton()
}
function onPointerUp(): void {
  isActive.value = false
}
function unfocusButton(): void {
  if (buttonEl.value) {
    buttonEl.value.blur()
  }
}

const buttonRef = ref<typeof OzBaseButton | null>(null)
const buttonEl = computed(() => buttonRef.value?.$el)

const isSvgIcon = computed(() => iconName.value != null && SVG_ICONS.includes(iconName.value))
const iconColor = computed((): string | null => {
  if (props.iconColor) {
    return props.iconColor
  }
  // If there is a secondaryMenu and secondaryMenuSelectionId, get the color from the secondaryMenu
  if (props.secondaryMenu && props.secondarySelectionId) {
    const secondaryMenuSelection = props.secondaryMenu.items.find(
      (menuItem) => menuItem.id === props.secondarySelectionId,
    )
    if (secondaryMenuSelection) {
      return secondaryMenuSelection.id
    }
  }
  return null
})

const iconColorClass = computed(() => (iconColor.value != null ? getTailwindColorClass(iconColor.value) : null))
const currentToolPreviewColor = computed(() => {
  const isDarkMode = props.colorScheme === 'dark'
  const isLightMode = props.colorScheme === 'light'
  const isDarkText100 = props.currentToolColor === 'dark-text-100'
  const isLightText100 = props.currentToolColor === 'light-text-100'

  if (!isDarkText100 && !isLightText100) {
    return props.currentToolColor ?? 'transparent'
  }
  if (isDarkMode && isDarkText100) {
    if (isHovered.value && !props.isSelected) {
      return 'grey-600'
    }
    if (props.isSelected) {
      return 'grey-700'
    }
  }

  if (isLightMode && isLightText100) {
    if (isHovered.value && !props.isSelected) {
      return 'grey-200'
    }
    if (props.isSelected) {
      return 'grey-100'
    }
  }
  return props.currentToolColor ?? 'transparent'
})
const currentToolPreviewColorHex = computed(() => {
  return tailwindFlattenedColors[currentToolPreviewColor.value]
})
const cornerIndicatorNeedsDiagonalLine = computed(() => {
  if (
    props.currentToolColor === 'dark-text-100' &&
    ((props.colorScheme === 'dark' && !props.isSelected) || (props.colorScheme === 'light' && props.isSelected))
  ) {
    return true
  }

  if (
    props.currentToolColor === 'light-text-100' &&
    ((props.colorScheme === 'light' && !props.isSelected) || (props.colorScheme === 'dark' && props.isSelected))
  ) {
    return true
  }
  return false
})
/**
 * For geo tool, we render the selected geo tool (e.g. rectangle, oval, since there is no generic geo icon
 * For color, we would use the iconColor passed in from props. (@see WhiteboardToolbar.vue )
 */
const getSecondaryMenuProperty = (property: 'iconName' | 'label' | 'shortcut'): string | undefined => {
  if (
    (props.iconName === 'geo' || props.iconName === 'draw-nested') &&
    props.secondaryMenu &&
    props.secondarySelectionId
  ) {
    const secondaryMenuSelection = props.secondaryMenu.items.find(
      (menuItem) => menuItem.id === props.secondarySelectionId,
    )
    return secondaryMenuSelection ? secondaryMenuSelection[property] : props[property]
  }
  return props[property]
}

const iconName = computed(() => getSecondaryMenuProperty('iconName'))
const label = computed(() => getSecondaryMenuProperty('label'))
const shortcut = computed(() => getSecondaryMenuProperty('shortcut'))

const numberOfAttachmentTypesAllowed = computed(() => usePostContentPickerSources().contentSources.value.flat().length)

const isTouch = isTouchScreen()
const isRtl = dir() === 'rtl'

return { __sfc: true,whiteboardToolbarStore, whiteboardStore, props, emit, isHovered, isActive, onPointerEnter, onPointerLeave, onPointerDown, onPointerUp, unfocusButton, buttonRef, buttonEl, isSvgIcon, iconColor, iconColorClass, currentToolPreviewColor, currentToolPreviewColorHex, cornerIndicatorNeedsDiagonalLine, getSecondaryMenuProperty, iconName, label, shortcut, numberOfAttachmentTypesAllowed, isTouch, isRtl, OzBaseButton, OzBasePopover, OzBasePopoverColors, OzIcon, WhiteboardSvgIcon, offset, Portal }
}

})